import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";

/* Anonymized archetype testimonials — compliance-safe (no real client names). */
const TESTIMONIALS = [
    {
        quote:
            "Nyra drafts responses grounded in our own policy and past cases — our agents just review and send. First-contact resolution climbed and escalations dropped inside a quarter.",
        role: "Head of Digital Servicing",
        org: "Top-3 Private Bank",
        metric: "62%",
        metricLabel: "faster email response",
        icon: "fa-solid fa-building-columns",
        accent: "#2E7D6B",
    },
    {
        quote:
            "Members get instant, accurate answers around the clock, and every reply is traceable to the source document. It reduced staff burden without ever compromising trust.",
        role: "VP, Customer Experience",
        org: "National Health Payer",
        metric: "89%",
        metricLabel: "first-contact resolution",
        icon: "fa-solid fa-stethoscope",
        accent: "#5FA88A",
    },
    {
        quote:
            "Collections conversations now run end-to-end across WhatsApp and voice. Our people only touch the judgement-heavy cases — productivity has more than tripled.",
        role: "Director of Collections",
        org: "Leading NBFC Lender",
        metric: "3.4×",
        metricLabel: "agent productivity",
        icon: "fa-solid fa-hand-holding-dollar",
        accent: "#235F52",
    },
    {
        quote:
            "Twelve years of underwriting circulars became one governed knowledge layer. Audit preparation that used to take six weeks now takes days.",
        role: "Chief Digital Officer",
        org: "Public-Sector Insurer",
        metric: "6 wks → 4 days",
        metricLabel: "audit prep time",
        icon: "fa-solid fa-umbrella",
        accent: "#4A9683",
    },
    {
        quote:
            "Citizens are served in their own language, day or night, and grievances route to the right desk automatically. Wait times effectively disappeared.",
        role: "Head of Citizen Services",
        org: "Central Govt Department",
        metric: "24×7",
        metricLabel: "multilingual service",
        icon: "fa-solid fa-landmark",
        accent: "#A8B36E",
    },
    {
        quote:
            "Billing, plan changes and complaints are handled across every channel with shared context. Average handle time fell sharply while CSAT held steady.",
        role: "GM, Customer Support",
        org: "Tier-1 Telecom",
        metric: "40%",
        metricLabel: "lower handle time",
        icon: "fa-solid fa-tower-broadcast",
        accent: "#D4B542",
    },
];

const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
};

export const TestimonialCarousel = () => {
    const [[index, dir], setState] = useState([0, 0]);
    const paused = useRef(false);
    const n = TESTIMONIALS.length;

    const go = useCallback(
        (next) => setState(([cur]) => [(next + n) % n, next > cur ? 1 : -1]),
        [n],
    );
    const prev = () => setState(([cur]) => [(cur - 1 + n) % n, -1]);
    const next = () => setState(([cur]) => [(cur + 1) % n, 1]);

    useEffect(() => {
        const id = setInterval(() => {
            if (!paused.current) setState(([cur]) => [(cur + 1) % n, 1]);
        }, 6000);
        return () => clearInterval(id);
    }, [n]);

    const t = TESTIMONIALS[index];

    return (
        <section className="py-20 md:py-28 bg-clean" data-testid="testimonial-carousel">
            <div className="container-x">
                <Reveal>
                    <div className="text-sm text-slate-500 mb-4 text-center">
                        Trusted by category leaders in regulated industries
                    </div>
                    <h2 className="heading-section text-3xl md:text-5xl text-center mb-14">
                        In their words. <span className="italic">In their numbers.</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.1}>
                    <div
                        className="max-w-5xl mx-auto"
                        onMouseEnter={() => (paused.current = true)}
                        onMouseLeave={() => (paused.current = false)}
                    >
                        <div className="mesh-frame mesh-b relative overflow-hidden" style={{ padding: 0 }}>
                            <div className="glass-card m-2 md:m-3 p-7 md:p-12 min-h-[360px] md:min-h-[320px] flex flex-col">
                                <AnimatePresence mode="wait" custom={dir}>
                                    <motion.div
                                        key={index}
                                        custom={dir}
                                        variants={variants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                        className="grid md:grid-cols-12 gap-8 md:gap-10 items-center flex-1"
                                        data-testid="testimonial-card"
                                    >
                                        {/* Stat block */}
                                        <div className="md:col-span-4">
                                            <div
                                                className="icon-sticker w-12 h-12 mb-5"
                                                style={{ "--st-color": t.accent }}
                                            >
                                                <i className={`${t.icon} text-lg`} />
                                            </div>
                                            <div
                                                className="text-5xl md:text-6xl leading-none tabular-nums text-slate-900"
                                                style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: "-0.02em" }}
                                            >
                                                {t.metric}
                                            </div>
                                            <div className="mt-3 text-slate-600 text-sm leading-snug">
                                                {t.metricLabel}
                                            </div>
                                        </div>

                                        {/* Quote block */}
                                        <div className="md:col-span-8">
                                            <i
                                                className="fa-solid fa-quote-left text-2xl mb-4 block"
                                                style={{ color: t.accent }}
                                            />
                                            <p className="text-slate-800 text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                                {t.quote}
                                            </p>
                                            <div className="mt-6 pt-6 border-t border-slate-200/70">
                                                <div className="text-slate-900 font-medium text-sm">{t.role}</div>
                                                <div className="text-slate-500 text-xs mt-0.5 uppercase tracking-[0.14em]">
                                                    {t.org}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="mt-8 flex items-center justify-center gap-5">
                            <button
                                onClick={prev}
                                aria-label="Previous testimonial"
                                className="w-11 h-11 grid place-items-center rounded-full bg-white border border-slate-200 text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-all"
                                data-testid="testimonial-prev"
                            >
                                <i className="fa-solid fa-arrow-left text-sm" />
                            </button>

                            <div className="flex items-center gap-2.5">
                                {TESTIMONIALS.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => go(i)}
                                        aria-label={`Go to testimonial ${i + 1}`}
                                        data-testid={`testimonial-dot-${i}`}
                                        className="h-2 rounded-full transition-all"
                                        style={{
                                            width: i === index ? 26 : 8,
                                            background: i === index ? "#235f52" : "#cbd5cc",
                                        }}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={next}
                                aria-label="Next testimonial"
                                className="w-11 h-11 grid place-items-center rounded-full bg-white border border-slate-200 text-slate-700 hover:border-slate-900 hover:text-slate-900 transition-all"
                                data-testid="testimonial-next"
                            >
                                <i className="fa-solid fa-arrow-right text-sm" />
                            </button>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mt-10 text-center">
                        <a
                            href="/customer-stories"
                            className="text-sm text-slate-700 hover:text-slate-900 inline-flex items-center gap-2 font-medium transition-colors"
                            data-testid="testimonial-all-stories"
                        >
                            Read all customer stories
                            <i className="fa-solid fa-arrow-right text-xs" />
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default TestimonialCarousel;
